import { useState } from "react";
import { Send, Mail } from "lucide-react";
import { toast } from "sonner";

const ContactSection = () => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const mailtoLink = `mailto:graphiclines.pk@gmail.com?subject=${encodeURIComponent(`[Website] ${form.subject}`)}&body=${encodeURIComponent(`Name: ${form.firstName} ${form.lastName}\nEmail: ${form.email}\n\n${form.message}`)}`;
      window.open(mailtoLink, "_blank");
      toast.success("Opening your email client to send the message!");
      setForm({ firstName: "", lastName: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-12 md:py-20">
      <div className="container mx-auto px-4 max-w-2xl">
        
        {/* Header Area */}
        <div className="text-center mb-8 md:mb-12">
          <span className="text-xs font-semibold tracking-widest uppercase text-primary mb-2 block animate-fade-up">
            Get In Touch
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground mb-4 animate-fade-up delay-100">
            Contact Us
          </h2>
          <p className="text-muted-foreground text-sm md:text-base animate-fade-up delay-200">
            Fill out the form below or reach out to us directly via email or WhatsApp.
          </p>
        </div>

        {/* The Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4 bg-card border border-border rounded-2xl p-5 md:p-8 shadow-lg mb-8 md:mb-10 animate-fade-up delay-300">
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            <input type="text" placeholder="First Name *" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow" />
            <input type="text" placeholder="Last Name *" required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow" />
          </div>
          <input type="email" placeholder="Email Address *" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow" />
          <input type="text" placeholder="Subject *" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow" />
          <textarea placeholder="Comments / Questions *" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow resize-none" />
          <button type="submit" disabled={sending} className="w-full py-3 md:py-4 rounded-xl gradient-cta text-primary-foreground font-display font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 hover:-translate-y-0.5 shadow-lg transition-all duration-200 disabled:opacity-50 disabled:hover:translate-y-0 mt-2">
            {sending ? "Sending..." : "Send Message"}
            <Send className="w-4 h-4" />
          </button>
        </form>

        {/* Quick Contact Cards (Gmail & WhatsApp) */}
        <div className="grid sm:grid-cols-2 gap-4 animate-fade-up delay-400">
          
          {/* Direct Gmail Box */}
          <a 
            href="mailto:graphiclines.pk@gmail.com" 
            className="group flex flex-col items-center justify-center gap-3 p-6 rounded-2xl bg-card border border-border hover:border-primary/50 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-bold text-foreground text-sm md:text-base">Email Us</h3>
              <p className="text-xs text-muted-foreground mt-1 group-hover:text-foreground/80 transition-colors">graphiclines.pk@gmail.com</p>
            </div>
          </a>

          {/* WhatsApp Box */}
          <a 
            href="https://wa.me/+923058559591" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="group flex flex-col items-center justify-center gap-3 p-6 rounded-2xl bg-card border border-border hover:border-green-500/50 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center group-hover:scale-110 group-hover:bg-green-500/20 transition-all duration-300">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div className="text-center">
              <h3 className="font-bold text-foreground text-sm md:text-base">WhatsApp</h3>
              <p className="text-xs text-muted-foreground mt-1 group-hover:text-foreground/80 transition-colors">+92 305 8559591</p>
            </div>
          </a>

        </div>
      </div>
    </section>
  );
};

export default ContactSection;
